import { Drawer, Table, Typography } from "antd"
import type { ColumnsType } from "antd/es/table"
import React from "react"
import { useTranslation } from "react-i18next"

import { formatEducationFieldValue } from "./format-education-field-value.ts"
import type { EducationFieldView } from "./types/education.types.ts"

interface ReviewInputsDrawerProps {
  open: boolean
  fields: EducationFieldView[]
  onClose: () => void
}

/**
 * Screen 1's "Review inputs" table (spec §5, "Make the included-field list
 * visible") — one row per field the AI could draw on, and whether each guide
 * receives it. Kept as a controlled `open`/`onClose` component rather than
 * owning its own trigger, since Task 4's stale-fields banner opens the same
 * drawer from a different surface.
 */
export function ReviewInputsDrawer({ open, fields, onClose }: ReviewInputsDrawerProps) {
  const { t } = useTranslation()

  const renderIncluded = (included: boolean) =>
    included ? (
      <span aria-label={t("projectEducation.reviewDrawer.included")}>✓</span>
    ) : (
      <span aria-label={t("projectEducation.reviewDrawer.notIncluded")}>—</span>
    )

  const columns: ColumnsType<EducationFieldView> = [
    {
      title: t("projectEducation.reviewDrawer.columns.field"),
      dataIndex: "label",
      key: "label"
    },
    {
      title: t("projectEducation.reviewDrawer.columns.value"),
      dataIndex: "value",
      key: "value",
      render: (value: unknown) => (
        <span data-testid={"review-input-value"}>{formatEducationFieldValue(value, t) ?? "—"}</span>
      )
    },
    {
      title: t("projectEducation.reviewDrawer.columns.salesBrief"),
      dataIndex: "sales_person",
      key: "sales_person",
      align: "center",
      render: renderIncluded
    },
    {
      title: t("projectEducation.reviewDrawer.columns.customerGuide"),
      dataIndex: "customer",
      key: "customer",
      align: "center",
      render: renderIncluded
    }
  ]

  return (
    <Drawer
      title={t("projectEducation.reviewDrawer.title")}
      open={open}
      onClose={onClose}
      width={520}
      destroyOnHidden
    >
      <Typography.Paragraph type={"secondary"}>
        {t("projectEducation.reviewDrawer.note")}
      </Typography.Paragraph>
      <Table<EducationFieldView>
        rowKey={"key"}
        dataSource={fields}
        columns={columns}
        pagination={false}
        size={"small"}
      />
    </Drawer>
  )
}
